import React, { useMemo, useState, forwardRef } from 'react';
import { useTheme } from 'styled-components';

import { Text } from 'src/packages/core';
import PasswordHelper from './components/password-helper';
import { defaultPasswordConditions } from './components/password-helper/password-conditions';
import { PasswordShowIcon, PasswordHideIcon } from 'src/packages/icons';
import { DarkTheme, LightTheme } from 'src/packages/theme';

import { InputProps } from './Input.types';
import {
  StyledInputContainer,
  InputWrapper,
  StyledInput,
  ElementWrapper,
  getColorStyles,
} from './Input.styles';

const Input = forwardRef((props: InputProps, ref: React.ForwardedRef<HTMLInputElement>) => {
  const {
    label,
    helperText,
    helperTextOnFocus,
    errorMessage,
    patternMessage = 'Pattern mismatch',
    leftElement,
    rightElement,
    invalid,
    showPasswordHelp = false,
    passwordHelpSide = 'right-start',
    passwordConditions = defaultPasswordConditions,
    readOnly,
    placeholder = '',
    type = 'text',
    variant = 'primary',
    borderRadius = '6px',
    theme,
    onChange,
    pattern,
    ...rest
  } = props;
  const [passwordShown, setPasswordShown] = useState(false);
  const [internalValue, setInternalValue] = useState('');
  const [anchorEl, setAnchorEl] = useState<any>(null);

  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const togglePasswordVisiblity = () => {
    setPasswordShown(!passwordShown);
  };

  const hasPatternError = useMemo(() => {
    if (pattern) {
      const regex = new RegExp(`^(${pattern})$`);

      return props.value && !regex.test(props.value.toString());
    } else {
      return false;
    }
  }, [pattern, props.value]);

  const colorStyles = useMemo(() => {
    const isInvalid = !!errorMessage || invalid || !!hasPatternError;

    return getColorStyles(isInvalid, !!readOnly, defaultTheme);
  }, [errorMessage, hasPatternError, readOnly, defaultTheme, invalid]);

  const passwordToggleIcon = useMemo(() => {
    return (
      <ElementWrapper side={'right'} defaulttheme={defaultTheme} onClick={togglePasswordVisiblity}>
        {passwordShown ? <PasswordHideIcon /> : <PasswordShowIcon />}
      </ElementWrapper>
    );
  }, [passwordShown]);

  const onChangeHandler = (e: any) => {
    setInternalValue(e.target.value);
    onChange && onChange(e);
  };

  return (
    <StyledInputContainer
      {...rest}
      width={rest.width}
      onFocus={(e: any) => {
        setAnchorEl(e.currentTarget);
      }}
      onBlur={() => {
        setAnchorEl(null);
      }}
    >
      {label && <Text text={label} variant='p2' color={defaultTheme.colors.text.primary} />}

      <InputWrapper defaulttheme={defaultTheme}>
        {leftElement && (
          <ElementWrapper side={'left'} defaulttheme={defaultTheme}>
            {leftElement}
          </ElementWrapper>
        )}

        <StyledInput
          {...rest}
          defaulttheme={defaultTheme}
          readOnly={readOnly}
          ref={ref}
          borderRadius={borderRadius}
          type={passwordShown ? 'text' : type}
          placeholder={type === 'password' ? '* * * * * * * *' : placeholder}
          leftElement={leftElement}
          rightElement={type === 'password' ? passwordToggleIcon : rightElement}
          colorStyles={colorStyles}
          variant={variant}
          onChange={onChangeHandler}
          onWheel={(e) => {
            if (type === 'number') {
              (e.target as HTMLElement).blur();
            }
          }}
        />

        {type === 'password' ? (
          passwordToggleIcon
        ) : rightElement ? (
          <ElementWrapper side={'right'} defaulttheme={defaultTheme}>
            {' '}
            {rightElement}
          </ElementWrapper>
        ) : null}
      </InputWrapper>

      {helperText && typeof helperText === 'string' && !errorMessage && (
        <Text text={helperText} variant='p3' color={defaultTheme.colors.text.secondary} />
      )}

      {helperText && typeof helperText !== 'string' && !errorMessage ? helperText : null}

      {helperTextOnFocus && anchorEl && !errorMessage && (
        <Text text={helperTextOnFocus} variant='p3' color={defaultTheme.colors.palette.primary} />
      )}

      {errorMessage && !pattern && !hasPatternError && (
        <Text text={errorMessage} variant='p3' color={defaultTheme.colors.palette.red} />
      )}

      {pattern && hasPatternError && !showPasswordHelp && (
        <Text text={patternMessage} variant='p3' color={defaultTheme.colors.palette.red} />
      )}

      {showPasswordHelp && anchorEl && type === 'password' && internalValue && (
        <PasswordHelper
          placement={passwordHelpSide}
          passwordConditions={passwordConditions}
          anchorEl={anchorEl}
          id={'password-popper'}
          password={internalValue}
        />
      )}
    </StyledInputContainer>
  );
});

export default Input;
